import { ReactElement, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useElementHistory } from '@hooks'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { Stack, Tooltip, ToggleButtonGroup, ToggleButton, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useEditElementStore, useModelStore, roofStoreyKey } from '@editorStores'
import { useBlockScene, useElementLabel, useResultsInvalidation } from '@editorHooks'
import { CollapsibleSettingsBoxSection } from '@editorComponents'
import { useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getModel, getVerticalTransmissionGraph, getProjectFiles, getElementLoads } from '@queries'
import { createVerticalRoofSlab, deleteVerticalRoofSlab, updateVerticalRoofSlab } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { documentationRootGuid } from 'src/constants/files'
import PlanSettingsForm from '../../../PlanSettingsForm'
import { RectangularShapeForm } from '../../../RectangularShape'
import SplitVerticalSlabLikeForm from '../../../SplitVerticalSlabLikeForm'

interface Props {
  selectedElement?: string
  geometryEditable?: boolean
  activeStorey?: string
  showDrawRoofOnElement?: boolean
  onClose: () => void
}

const VerticalRoofForm = ({
  selectedElement,
  geometryEditable = true,
  activeStorey,
  showDrawRoofOnElement = false,
  onClose,
}: Props): ReactElement | null => {
  const drawRoofOnElement = useEditElementStore(state => state.drawRoofOnElement)
  const setDrawRoofOnElement = useEditElementStore(state => state.setDrawRoofOnElement)

  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const removeVerticalRoofSlab = useModelStore(state => state.removeVerticalRoofSlab)
  const updateStorey = useModelStore(state => state.updateStorey)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const client = useQueryClient()

  const invalidateResults = useResultsInvalidation()

  const getLabel = useElementLabel()

  const verticalRoofSlab: VerticalRoofSlab = useMemo(() => {
    return find(verticalRoofSlabs, ['guid', selectedElement]) as VerticalRoofSlab
  }, [verticalRoofSlabs, selectedElement])

  const updateVerticalRoof = useModelStore(state => state.updateVerticalRoof)

  const model = useModelStore(state => state.model)

  const storeys = useMemo(() => [...Object.keys(model.storey_boundaries), roofStoreyKey], [model])

  useEffect(() => {
    // remove vertical roof slab when component unmounts
    // when vertical roof slab.is_local is still true
    // this means that the element was not saved
    return () => {
      const roofs = useModelStore.getState().model.vertical_roof_slabs
      const roof = find(roofs, ['guid', selectedElement])

      // slab has to be saved otherwise remove
      if (roof?.is_local) removeVerticalRoofSlab(roof.guid)
    }
  }, [removeVerticalRoofSlab, selectedElement])

  const { setStale: setRoofStale } = useElementHistory({
    element: verticalRoofSlab,
    getCurrentElement: () => {
      const verticalRoofSlabs = useModelStore.getState().model.vertical_roof_slabs
      return find(verticalRoofSlabs, ['guid', selectedElement]) as ShapeObject
    },
    removeElement: verticalRoofSlab => removeVerticalRoofSlab(verticalRoofSlab.guid),
    resetElement: verticalRoofSlab => updateVerticalRoof(verticalRoofSlab),
    dependencies: [selectedElement],
  })

  useStructuralPlanningFormEsc(() => {
    const roofs = useModelStore.getState().model.vertical_roof_slabs
    const roof = find(roofs, ['guid', selectedElement])
    // slab has to be saved otherwise remove
    if (roof?.is_local) {
      removeVerticalRoofSlab(roof.guid)
    }
    onClose()
  }, !!selectedElement)

  // mutations

  const { mutateAsync: handleCreate, isLoading: isCreating } = useMutation(
    (purlin: ShapeObject) => createVerticalRoofSlab.request(projectId, purlin),
    {
      onSuccess: async data => {
        setRoofStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Dach erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Daches'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleUpdate, isLoading: isUpdating } = useMutation(
    (roofSlab: ShapeObject) => updateVerticalRoofSlab.request(projectId, roofSlab),
    {
      onSuccess: async data => {
        setRoofStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Dach erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Daches'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (purlinGuid: string) => deleteVerticalRoofSlab.request(projectId, purlinGuid),
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Dach erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Daches'), {
          variant: 'error',
        })
      },
    },
  )

  useBlockScene(isCreating || isDeleting || isUpdating)

  const ElementSwitch = () =>
    showDrawRoofOnElement ? (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ m: 2 }}>
        <Tooltip title="Wenn diese Einstellung auf 'auf Element zeichnen' steht, wird das Element auf welchem die Maus bei Starten des Zeichnens ruht als Referenzelement für die Zeichenebene genutzt. Andernfalls wird das aktive Stockwerk als Referenz verwendet. ">
          <ToggleButtonGroup
            value={drawRoofOnElement}
            exclusive
            onChange={(_, value) => {
              // when the value is null, it means that the toggle button is not selected
              // this leads to the effect that an already enabled button stays enabled
              value !== null && setDrawRoofOnElement(value)
            }}
            fullWidth={true}
            size="small"
          >
            <ToggleButton
              value={false}
              aria-label="is horizontal purlin"
              fullWidth={true}
              sx={{ paddingY: 0 }}
            >
              <Typography>Auf Stockwerk zeichnen</Typography>
            </ToggleButton>
            <ToggleButton
              value={true}
              aria-label="is non horizontal rafter"
              fullWidth={true}
              sx={{ paddingY: 0 }}
            >
              <Typography>Auf Element zeichnen</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
      </Stack>
    ) : (
      <></>
    )

  if (!verticalRoofSlab) return <ElementSwitch />

  return (
    <>
      <ElementSwitch />

      <RectangularShapeForm
        element={verticalRoofSlab}
        onCreate={handleCreate}
        onDelete={handleDelete}
        onUpdate={handleUpdate}
        onDeleteLocal={removeVerticalRoofSlab}
        initialTab={'geometry'}
        label={getLabel(verticalRoofSlab.guid)}
        geometryEditable={geometryEditable}
        isDeleting={isDeleting}
        updateShape={updateVerticalRoof}
        storeys={storeys}
        updateStorey={updateStorey}
        activeStorey={activeStorey}
        isSubmitting={isCreating || isUpdating}
        onClose={onClose}
        settingsSection={
          <CollapsibleSettingsBoxSection defaultExpanded={true}>
            <PlanSettingsForm element={verticalRoofSlab} setStale={setRoofStale} />
          </CollapsibleSettingsBoxSection>
        }
        splitSection={
          selectedElement ? (
            <SplitVerticalSlabLikeForm
              element_guid={selectedElement}
              setSlabStale={setRoofStale}
              handleUpdate={handleUpdate}
              handleCreate={handleCreate}
            />
          ) : undefined
        }
      />
    </>
  )
}

export default VerticalRoofForm
